import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import {
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
} from "actions";
import {
  REGULAR_SEASON_IDS,
  isRegularSeasonId,
} from "../../../constants/pictreeRegularSeasonIds";

const REGULAR_BY_AREA_PAGE = "/game/pictree/ntt/photos/by-area";
const REGULAR_LAYER_OPTIONS = [
  { name: "都道府県", value: "regular_1_l1" },
  { name: "医療圏", value: "regular_1_l2" },
  { name: "市区町村", value: "regular_1_l3" },
];
const SINGLE_PERIOD_DEFAULT_SEASON_ID = "cmr0bus0w00010azches0f7ak";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const getDateOnly = (value) => String(value || "").split(" ")[0];
const clampSeasonEndToToday = (seasonEnd, todayFormatted) => {
  if (!seasonEnd) {
    return todayFormatted;
  }

  const endDate = getDateOnly(seasonEnd);
  return moment(endDate).isAfter(todayFormatted) ? todayFormatted : endDate;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
  })
);

const PeriodNtt = ({
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
  seasonData,
  selectedSeason,
  selectedAreaLayerId,
  dateRangePicTreeNtt,
  singlePeriod,
  currentPage,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const [seasonOption, setSeasonOption] = useState({ name: "", value: "" });

  const getSeasonById = useCallback(
    (seasonId) =>
      seasonData.find((season) => season.seasonId === seasonId) || null,
    [seasonData]
  );

  const applySeasonSelection = useCallback(
    (seasonId) => {
      const season = getSeasonById(seasonId);
      if (!season) {
        return false;
      }

      setSeasonOption({
        name: season.seasonName,
        value: season.seasonId,
      });
      setSelectedSeasonNtt([season]);

      const endDate = isRegularSeasonId(season.seasonId)
        ? clampSeasonEndToToday(season.seasonEnd, todayFormatted)
        : getDateOnly(season.seasonEnd);

      setDateRangePicTreeNtt([
        getDateOnly(season.seasonStart),
        endDate,
      ]);
      setDateRangePicTreeMinMax([
        getDateOnly(season.seasonStart),
        endDate,
      ]);

      if (isRegularSeasonId(season.seasonId)) {
        setSelectedAreaLayerNtt("regular_1_l1");
      }

      return true;
    },
    [
      getSeasonById,
      setDateRangePicTreeNtt,
      setDateRangePicTreeMinMax,
      setSelectedSeasonNtt,
      setSelectedAreaLayerNtt,
      todayFormatted,
    ]
  );

  useEffect(() => {
    getSeasonDataNtt();
  }, [getSeasonDataNtt]);

  useEffect(() => {
    if (seasonData.length === 0) {
      return;
    }

    const selectedSeasonId = selectedSeason?.[0]?.seasonId;
    if (selectedSeasonId) {
      const selected = getSeasonById(selectedSeasonId);
      if (selected) {
        setSeasonOption({
          name: selected.seasonName,
          value: selected.seasonId,
        });
        return;
      }
    }

    const defaultSeason =
      getSeasonById(SINGLE_PERIOD_DEFAULT_SEASON_ID) || seasonData[0];
    if (defaultSeason) {
      applySeasonSelection(defaultSeason.seasonId);
    }
  }, [seasonData, selectedSeason, getSeasonById, applySeasonSelection]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        // !moment(option.seasonStart).isAfter(currentDateTime) &&
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));
      const selectedLatestEnd = selectedSeasonsData.some((season) => isRegularSeasonId(season.seasonId))
        ? clampSeasonEndToToday(latestEnd, todayFormatted)
        : getDateOnly(latestEnd);

      setDateRangePicTreeMinMax([
        getDateOnly(earliestStart),
        selectedLatestEnd,
      ]);
      setDateRangePicTreeNtt([
        getDateOnly(earliestStart),
        selectedLatestEnd,
      ]);

      // ステートを更新
      setSelectedSeasonNtt(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeNtt([]);
    }
  };

  const handleChange = (event) => {
    applySeasonSelection(event.target.value);
  };

  const handleLayerChange = (event) => {
    setSelectedAreaLayerNtt(event.target.value);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div style={{ width: 300, marginTop: -12 }}>
                <SingleSelect
                  selected={seasonOption}
                  options={[
                    { name: "北海道シーズン 2026", value: "cmr0bus0w00010azches0f7ak" },
                    { name: "レギュラーシーズン2", value: REGULAR_SEASON_IDS[1] || REGULAR_SEASON_IDS[0] },
                    { name: "レギュラーシーズン1", value: REGULAR_SEASON_IDS[0] },
                    { name: "東京・神奈川・千葉シーズン", value: "cmkdvml9l0001ngs48zepg7ne" },
                    { name: "栃木シーズン", value: "cmg7iw3vc00037bowekjy65pc" },
                    { name: "長野・山梨・埼玉シーズン", value: "cmg7iw3va00017bow596s39cs" },
                    { name: "北海道シーズン 2025", value: "cmb68qvei00011vo0e591229z" },
                    { name: "岩手・宮城・福島・新潟シーズン", value: "cmd5oyfft0001iho10o2iceha" },
                    { name: "青森・秋田・山形シーズン", value: "cmadtlnih0001n3mx1wvl9zog" },
                    { name: "北関東シーズン", value: "cm64mqm94000br3n0esu8c0n4" },
                    { name: "秋田県北 & 県央・県南シーズン", value: "cm46zidp30003bpp6cxq101mo" }
                  ]}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
          {REGULAR_SEASON_IDS.includes(seasonOption.value) &&
            currentPage === REGULAR_BY_AREA_PAGE && (
            <>
            <Grid item spacing={1}>
              階層：
            </Grid>
            <Grid item>
              <div style={{ width: 220, marginTop: -12 }}>
                <SingleSelect
                  selected={{ value: selectedAreaLayerId }}
                  options={REGULAR_LAYER_OPTIONS}
                  handleChange={handleLayerChange}
                />
              </div>
            </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

PeriodNtt.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeNtt.seasonData,
  selectedSeason: state.pictreeNtt.selectedSeason,
  selectedAreaLayerId: state.pictreeNtt.selectedAreaLayerId,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  currentPage: state.page.currentPage,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNtt: (param) => dispatch(setDateRangePicTreeNtt(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataNtt: () => dispatch(getSeasonDataNtt()),
  setSelectedSeasonNtt: (param) => dispatch(setSelectedSeasonNtt(param)),
  setSelectedAreaLayerNtt: (param) => dispatch(setSelectedAreaLayerNtt(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodNtt));
